/**
 * The studio, production and playout routes of the route table (`routes.ts`
 * spreads these into `ROUTES`): PLAN §5.1's Studio → Production, §5.3's
 * operator commands and §6.6's playout state and as-run log.
 *
 * Served by BOTH hosts through `@acp/handlers` (studios and productions are
 * stored on each; the node's replicate upward with the site link, phase 4).
 * The playout routes reach the production process through the
 * `PlayoutGateway` port: on a node, the local process (wave 3b); in the cloud,
 * nothing yet — they answer `unavailable` until the site link can carry a
 * command to a site's production (phase 4).
 *
 * Every permission is checked on the resource (D14): a studio is its own
 * resource (`studioId`, `siteId`), and a production is its studio's.
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import {
  asRunQuerySchema,
  asRunResponseSchema,
  commandResultSchema,
  playoutCommandSchema,
  playoutStateResponseSchema,
  PLAYOUT_COMMAND_PERMISSIONS,
} from './playout.js';
import {
  createProductionRequestSchema,
  endProductionRequestSchema,
  listProductionsQuerySchema,
  listProductionsResponseSchema,
  productionPathSchema,
  productionResponseSchema,
} from './productions.js';
import type { RouteDef } from './routes.js';
import {
  createStudioRequestSchema,
  deleteStudioQuerySchema,
  listStudiosResponseSchema,
  studioPathSchema,
  studioResponseSchema,
  updateStudioRequestSchema,
} from './studios.js';

const CONFIG_VALIDATION =
  'The config is validated against the studio-config schema of the engine (devices, mappings, source/output layers, piece types, actions, adlib panels; PLAN §7.1a); a failure is `validation-failed` with issues under `config.`.';

export const OPERATIONS_ROUTES = {
  createStudio: {
    id: 'createStudio',
    method: 'POST',
    path: '/studios',
    tag: 'studios',
    summary: 'Create a studio',
    description: `Creates a studio at version 1. Supplying \`studioId\` makes a retry idempotent: a studio with that id is returned as it is. ${CONFIG_VALIDATION}`,
    params: null,
    query: null,
    body: createStudioRequestSchema,
    response: studioResponseSchema,
    successStatus: 201,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listStudios: {
    id: 'listStudios',
    method: 'GET',
    path: '/studios',
    tag: 'studios',
    summary: 'List studios',
    description: 'The tenant’s studios the caller may read, by id, without their configs.',
    params: null,
    query: null,
    body: null,
    response: listStudiosResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: [],
  },
  getStudio: {
    id: 'getStudio',
    method: 'GET',
    path: '/studios/{studioId}',
    tag: 'studios',
    summary: 'Get a studio',
    description: 'The studio with its config and the `version` an update must name.',
    params: studioPathSchema,
    query: null,
    body: null,
    response: studioResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  updateStudio: {
    id: 'updateStudio',
    method: 'PUT',
    path: '/studios/{studioId}',
    tag: 'studios',
    summary: 'Replace a studio',
    description: `Replaces name, site and config, if the studio is still at \`version\` (\`conflict\` otherwise: re-read, re-apply, retry), and bumps the version. Productions already created keep their snapshot (D22). ${CONFIG_VALIDATION}`,
    params: studioPathSchema,
    query: null,
    body: updateStudioRequestSchema,
    response: studioResponseSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteStudio: {
    id: 'deleteStudio',
    method: 'DELETE',
    path: '/studios/{studioId}',
    tag: 'studios',
    summary: 'Delete a studio',
    description:
      'Deletes the studio and returns it as it was. `?version=` makes it conditional. Productions created from it are unaffected: they run from their own snapshot (D22).',
    params: studioPathSchema,
    query: deleteStudioQuerySchema,
    body: null,
    response: studioResponseSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  createProduction: {
    id: 'createProduction',
    method: 'POST',
    path: '/productions',
    tag: 'productions',
    summary: 'Create a production',
    description:
      'Creates a production of a playlist (or a single rundown) in a studio, status `created`, with a snapshot of the studio’s config as it is now (D22). `packageHash` is null until the node runtime pins one (PLAN §4.1, phase 4). Supplying `productionId` makes a retry idempotent.',
    params: null,
    query: null,
    body: createProductionRequestSchema,
    response: productionResponseSchema,
    successStatus: 201,
    permission: 'production.launch.onprem',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  listProductions: {
    id: 'listProductions',
    method: 'GET',
    path: '/productions',
    tag: 'productions',
    summary: 'List productions',
    description: 'The tenant’s productions the caller may read, newest first, without their config snapshots, in pages.',
    params: null,
    query: listProductionsQuerySchema,
    body: null,
    response: listProductionsResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request'],
  },
  getProduction: {
    id: 'getProduction',
    method: 'GET',
    path: '/productions/{productionId}',
    tag: 'productions',
    summary: 'Get a production',
    description: 'The production record with its config snapshot.',
    params: productionPathSchema,
    query: null,
    body: null,
    response: productionResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  endProduction: {
    id: 'endProduction',
    method: 'POST',
    path: '/productions/{productionId}/end',
    tag: 'productions',
    summary: 'End a production',
    description:
      'Ends the production (terminal: it takes no more commands). An active production must be deactivated first (`conflict`), so nothing on air is ended by accident. Ending an ended production returns it as it is.',
    params: productionPathSchema,
    query: null,
    body: endProductionRequestSchema,
    response: productionResponseSchema,
    successStatus: 200,
    permission: 'production.launch.onprem',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  sendPlayoutCommand: {
    id: 'sendPlayoutCommand',
    method: 'POST',
    path: '/productions/{productionId}/commands',
    tag: 'playout',
    summary: 'Send an operator command',
    description:
      'One operator command (PLAN §5.3, D19) to the production’s active process: `activate`, `deactivate`, `reset` (need `playout.activate`), `take`, `setNext`, `moveNext`, `setNextSegment`, `queueNextSegment`, `adlib`, `stopPiecesOnLayer`, `disableNextPiece`, `executeAction`, `syncToInstance` (need `playout.take`). ' +
      'The answer is an ack with the playout `seq` that includes the command, or a nack with a `reason` — both 200. A repeated `commandId` gets the first answer again and is not run twice. ' +
      '`unavailable` when the production process cannot be reached — in the cloud always, until the site link (phase 4).',
    params: productionPathSchema,
    query: null,
    body: playoutCommandSchema,
    response: commandResultSchema,
    successStatus: 200,
    permission: null,
    permissionOneOf: PLAYOUT_COMMAND_PERMISSIONS,
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'forbidden'],
  },
  getPlayout: {
    id: 'getPlayout',
    method: 'GET',
    path: '/productions/{productionId}/playout',
    tag: 'playout',
    summary: 'The playout state',
    description:
      'The production’s playout state now (PLAN §5.2, §6.6): activation, previous/current/next part instances with their piece instances, timings, device statuses and the `seq`. Live changes: the WS `playout.subscribe`. `unavailable` when the production process cannot be reached (in the cloud always, until phase 4).',
    params: productionPathSchema,
    query: null,
    body: null,
    response: playoutStateResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  listAsRun: {
    id: 'listAsRun',
    method: 'GET',
    path: '/productions/{productionId}/asrun',
    tag: 'playout',
    summary: 'The as-run log',
    description: 'The production’s append-only as-run events (PLAN §5.4), oldest first, after `cursor`.',
    params: productionPathSchema,
    query: asRunQuerySchema,
    body: null,
    response: asRunResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
} as const satisfies Record<string, RouteDef>;
